import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  BookOpen,
  Info,
  HelpCircle,
  Shield,
  FileText,
} from "lucide-react";

const navLinks = [
  { to: "/how-to-play", label: "How to Play", icon: BookOpen },
  { to: "/about", label: "About", icon: Info },
  { to: "/faq", label: "FAQ", icon: HelpCircle },
];

const legalLinks = [
  { to: "/privacy", label: "Privacy", icon: Shield },
  { to: "/terms", label: "Terms", icon: FileText },
];

export default function HamburgerMenu() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const close = useCallback(() => setOpen(false), []);

  // Close on route change
  useEffect(() => {
    close();
  }, [location.pathname, close]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, close]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center justify-center rounded-xl p-1.5 text-text-secondary transition-colors hover:bg-surface-sunken hover:text-text"
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {open &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40 bg-black/40"
              onClick={close}
            />

            {/* Drawer */}
            <div className="fixed top-0 right-0 z-50 flex h-full w-64 flex-col bg-surface shadow-xl">
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <span className="font-display text-sm font-bold text-text-secondary uppercase tracking-widest">
                  Menu
                </span>
                <button
                  onClick={close}
                  className="rounded-lg p-1 text-text-secondary transition-colors hover:bg-surface-sunken hover:text-text"
                  aria-label="Close menu"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <nav className="flex-1 overflow-y-auto px-2 py-3">
                <ul className="space-y-1">
                  {navLinks.map(({ to, label, icon: Icon }) => {
                    const active = location.pathname === to;
                    return (
                      <li key={to}>
                        <Link
                          to={to}
                          className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                            active
                              ? "bg-primary/5 text-primary"
                              : "text-text-secondary hover:bg-surface-sunken hover:text-text"
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                          {label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>

                <hr className="my-3 border-border" />

                <ul className="space-y-1">
                  {legalLinks.map(({ to, label, icon: Icon }) => {
                    const active = location.pathname === to;
                    return (
                      <li key={to}>
                        <Link
                          to={to}
                          className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                            active
                              ? "bg-primary/5 text-primary"
                              : "text-text-secondary hover:bg-surface-sunken hover:text-text"
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                          {label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
